/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useState } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { useRouterActions } from 'react-resource-router';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button/button';
import { cssMap, jsx } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import PageIcon from '@atlaskit/icon/core/page';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import Motion from '@atlaskit/motion/entering/motion';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';
import { HelpPageAri } from '@atlassian/ari/help';

import type { moreDropdown_AtlassianHubAppShell$key } from './__generated__/moreDropdown_AtlassianHubAppShell.graphql';
import messages from './messages';

const styles = cssMap({
	overflowHidden: {
		overflow: 'hidden',
	},
	dropdownItems: {
		paddingLeft: token('space.100'),
	},
});

const motionStyles = cssMap({
	slideEntering: {
		animationName: `${token('motion.keyframe.slide.in.top.short')}, ${token('motion.keyframe.fade.in')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.out.practical'),
	},
	slideExiting: {
		animationName: `${token('motion.keyframe.slide.out.top.short')}, ${token('motion.keyframe.fade.out')}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.in.practical'),
	},
});

export const MoreDropdown = ({
	queryRef,
	textColor,
	isMobile,
}: {
	isMobile?: boolean;
	queryRef: moreDropdown_AtlassianHubAppShell$key | null;
	textColor?: string;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [isExpanded, setIsExpanded] = useState(false);

	const data = useFragment(
		graphql`
			fragment moreDropdown_AtlassianHubAppShell on HelpCenter {
				customPages: pages(filter: { ids: [], type: CUSTOM }) {
					id
					name {
						default
					}
				}
			}
		`,
		queryRef,
	);

	const customPages = data?.customPages;

	if (!customPages || customPages.length === 0) {
		return null;
	}

	if (isMobile) {
		return (
			<>
				<ButtonItem
					iconAfter={
						isExpanded ? (
							<ChevronUpIcon label={formatMessage(messages.moreUpIcon)} />
						) : (
							<ChevronDownIcon label={formatMessage(messages.moreDownIcon)} />
						)
					}
					onClick={() => setIsExpanded((prev) => !prev)}
					aria-expanded={isExpanded}
				>
					<Text weight="medium">{formatMessage(messages.more)}</Text>
				</ButtonItem>
				<div css={styles.overflowHidden}>
					<ExitingPersistence appear>
						{isExpanded && (
							<Motion
								enteringAnimationXcss={motionStyles.slideEntering}
								exitingAnimationXcss={motionStyles.slideExiting}
							>
								<div css={styles.dropdownItems} data-testid="more-dropdown-items">
									<Section>
										{customPages.map((page) => {
											const pageId = page?.id ? HelpPageAri.parse(page.id).pageId : '';
											return (
												<ButtonItem
													key={pageId}
													testId={`nav-custom-page-${pageId}`}
													onClick={() => {
														push(`/page/${pageId}`);
													}}
												>
													{page?.name?.default}
												</ButtonItem>
											);
										})}
									</Section>
								</div>
							</Motion>
						)}
					</ExitingPersistence>
				</div>
			</>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<Button
					{...props}
					appearance="subtle"
					style={{ color: props.isSelected ? undefined : textColor }}
					iconAfter={<ChevronDownIcon label={formatMessage(messages.moreDownIcon)} />}
					ref={triggerRef}
				>
					{formatMessage(messages.more)}
				</Button>
			)}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{customPages.map((page) => {
					const pageId = page?.id ? HelpPageAri.parse(page.id).pageId : '';
					return (
						<DropdownItem
							key={pageId}
							testId={`nav-custom-page-${pageId}`}
							elemBefore={<PageIcon label={formatMessage(messages.pageIconLabel)} />}
							onClick={() => {
								push(`/page/${pageId}`);
							}}
						>
							{page?.name?.default}
						</DropdownItem>
					);
				})}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
